<template>
  <ChartCard :data-widget-id="widgetId">
    <ChartHeader
      :widget-download-id="widgetId"
      tooltip-key="passthrough-number-of-candidates"
    >
      <template #title>
        <div class="calculated-label new-h2">
          How many candidates do I need to get <v-text-field
            v-model="desiredNumberOfHires"
            class="mx-1 my-0 yield-input"
            hide-details
            type="number"
            density="compact"
            :rules="yieldRules"
            @change="passthroughCalcUpdate"
          /> hires?
        </div>
      </template>
    </ChartHeader>
    <ChartContent
      height="293.5px"
      :empty-data="emptyData || noWidgetData"
      :error="error"
      :loading="loading"
      type="horizontalBarChart"
      class="px-3 pt-2 pb-0"
    >
      <BarChart :id="widgetId" :data="chartData" :options="chartOptions" :plugins="plugins" />
      <template #error>
        <NoHiresMade />
      </template>
    </ChartContent>
  </ChartCard>
</template>

<script setup>
import ChartHeader from '@/components/insights/Chart/ChartHeader.vue';
import ChartCard from '@/components/insights/Chart/ChartCard.vue';
import ChartContent from '@/components/insights/Chart/ChartContent.vue';
import BarChart from '@/components/insights/Chart/BarChart.vue';
import { computed, ref } from 'vue';
import { useColors } from '@/composables/colors';
import { customTooltip } from '@/components/insights/Chart/oldChartTooltip';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { useTracking } from '@/stores/tracking/tracking';
import { numberWithCommas } from '@/helpers/formatters';
import { dataLabelsConfig, basicYScaleConfig } from '@/api/helpers/insights/commonChartConfigs';
import NoHiresMade from '@/assets/no-hires-made-chair.svg?component';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  emptyData: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const { passthroughCalcUpdate } = useTracking();

const plugins = [ChartDataLabels];

const widgetId = 'yield-calculations';

const yieldRules = [
  (value) => !Number.isNaN(value),
  (value) => parseInt(value, 10) > 0,
];

const {
  stageScreen,
  stageAssessment,
  stageOffer,
  stageApplication,
  grey400,
} = useColors();

const calculatePassthrough = (value, total) => {
  if (total === 0) return 0;
  return value / total;
};

const numberOfHires = ref(5);

const desiredNumberOfHires = computed({
  get() {
    return numberOfHires.value;
  },
  set(newNumber) {
    numberOfHires.value = parseInt(newNumber, 10) || 1;
  },
});

const noWidgetData = computed(() => {
  const { total } = props.data || {};

  return !total || !total?.hired;
});

const chartData = computed(() => {
  const { total } = props.data || {};
  const {
    review: totalReview,
    screen: totalScreen,
    assessment: totalAssessment,
    offer: totalOffer,
    hired: totalHired,
  } = total || {};

  const reviewToScreenPassthrough = calculatePassthrough(totalScreen, totalReview);
  const screeningToAssessmentPassthrough = calculatePassthrough(totalAssessment, totalScreen);
  const assessmentToOfferPassthrough = calculatePassthrough(totalOffer, totalAssessment);
  const offerToHiredPassthrough = calculatePassthrough(totalHired, totalOffer);

  const offer = numberOfHires.value / offerToHiredPassthrough;
  const assessment = offer / assessmentToOfferPassthrough;
  const screening = assessment / screeningToAssessmentPassthrough;
  const applications = screening / reviewToScreenPassthrough;

  let data = [];

  if (totalHired) {
    data = [
      Math.round(applications),
      Math.round(screening),
      Math.round(assessment),
      Math.round(offer),
    ];
  }

  return {
    labels: ['Application', 'Screening', 'Assessment', 'Offered'],
    datasets: [
      {
        label: '',
        backgroundColor: [
          stageApplication.value,
          stageScreen.value,
          stageAssessment.value,
          stageOffer.value,
        ],
        borderColor: [
          stageApplication.value,
          stageScreen.value,
          stageAssessment.value,
          stageOffer.value,
        ],
        fill: false,
        maxBarThickness: 24,
        barThickness: 'flex',
        data,
      },
    ],
  };
});

const chartOptions = computed(() => ({
  maintainAspectRatio: false,
  responsive: true,
  indexAxis: 'y',
  plugins: {
    legend: { display: false },
    tooltip: {
      mode: 'index',
      enabled: false,
      external: customTooltip(
        'basicChart',
        {
          chartData: {
            originalLabels: chartData.value.labels,
            datasets: chartData.value.datasets,
          },
          isFlippedAxis: true,
          labelFormat: 'count',
        },
        widgetId,
      ),
      bodyFont: {
        size: 16,
        family: 'Inter',
      },
    },
    datalabels: {
      color: dataLabelsConfig.color,
      anchor: dataLabelsConfig.anchor,
      align: dataLabelsConfig.align,
      formatter: function formatterCallback(value) {
        if (Number.isNaN(value)) return '';

        return numberWithCommas(value);
      },
    },
  },
  scales: {
    x: {
      title: {
        display: false,
      },
      beginAtZero: true,
      border: {
        display: false,
        dash: [2, 3],
      },
      grid: {
        display: true,
        color: grey400.value,
        tickLength: 0,
      },
      ticks: {
        padding: 24,
      },
    },
    y: basicYScaleConfig,
  },
}));
</script>

<style scoped lang="less">
.yield-input {
  width: 60px;
  display: inline-block;
}

.calculated-label {
  max-height: 40px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;

  .v-text-field {
    height: 20px;

    :deep(.v-field__input) {
      min-height: 0;
      height: 20px;
      padding: 4px 0 6px;
      color: @text-darkest;
      font-weight: 300;
    }
  }
}

:deep(.v-field--variant-filled) {
  .v-field__overlay {
    background: @white;
  }

  .v-field__outline::before, .v-field__outline::after {
    border-color: @text-darkest;
  }

  &.v-field--focused .v-field__outline::before, &.v-field--focused .v-field__outline::after {
    border-color: @text-brand;
  }
}
</style>
